import { Button } from "../../../../components/ui/button";
import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import { useParams } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../../components/ui/form";
import { useToast } from "../../../../hooks/use-toast";
import { handleHttpError } from "../../../../utils/utils";
import Spinner from "../../../../components/spinner.components";
import { Input } from "../../../../components/ui/input";
import { useAddFixedPayoutToProposalAdminHttp } from "../../../../hooks/http/admin/proposal-creators/add-fixed-payout.admin.http";
import useProposalSocialStore from "../../../../store/proposal-creators.store";

const FormSchema = z.object({
  fixed_payout_amount: z.string().transform((val) => Number.parseFloat(val)),
});

export const InternalCostModalPartial: React.FunctionComponent = () => {
  const { toast } = useToast();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  const { proposalId, proposalSocialId } = useParams();
  const {updateInternalCost } =
    useProposalSocialStore();

  const { execute: addToFixedPayoutHttp, fetching: addingToFixedPayout } =
    useAddFixedPayoutToProposalAdminHttp();

  async function handleAddToFixedPayout(
    proposalSocialId: string,
    amount: number
  ) {
    if (!proposalSocialId) return;
    if (!proposalId) return;
    const response = await addToFixedPayoutHttp({
      body: {
        amount,
      },
      pathParams: { proposalId, proposalSocialId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    toast({
      content: "Internal cost set successfully",
      toastType: "success",
    });
      updateInternalCost(proposalSocialId, amount);
    document.getElementById("closeDialoug")?.click();
  }

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (!proposalSocialId) {
      return;
    }
    await handleAddToFixedPayout(proposalSocialId, data.fixed_payout_amount);
  }

  return (
    <DialogContent className="flex flex-col items-center gap-5 border-border w-full max-w-xl text-center text-primary">
      <DialogHeader className="w-full">
        <div className="flex flex-col">
          <p className="flex gap-1 font-semibold text-28 leading-8">
            Set internal cost
          </p>
        </div>
      </DialogHeader>
      <div className="border-b border-border w-full" />
      <div className="flex items-center gap-4 w-full">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="relative flex flex-col space-y-8 w-full"
          >
            <FormField
              control={form.control}
              name="fixed_payout_amount"
              render={({ field }) => (
                <FormItem className="text-start">
                  <FormControl>
                    <div className="relative w-full">
                      <span className="left-0 absolute inset-y-0 flex items-center pl-5 font-semibold text-xl">
                        ₹
                      </span>
                      <Input
                        {...field}
                        className="py-6 pl-10 w-full h-16 placeholder:text-secondary"
                        type="number"
                        placeholder="Enter internal cost"
                        min={0}
                      />
                      <div className="right-0 absolute inset-y-0 flex items-center h-full">
                        <button
                          type="button"
                          className="bg-tertiarybuttonbg px-5 h-full font-semibold text-xl"
                          onClick={() => {
                            field.onChange(
                              (Number(field.value) - 1).toString()
                            );
                          }}
                        >
                          -
                        </button>
                        <button
                          type="button"
                          className="bg-tertiarybuttonbg px-5 rounded-tr-10 rounded-br-10 h-full font-semibold text-xl"
                          onClick={() => {
                            field.onChange(
                              (Number(field.value) + 1).toString()
                            );
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={addingToFixedPayout}
              className="bg-primarybuttonbg w-40 h-14 font-semibold text-primarybuttontext tracking-wider self-end"
            >
              {addingToFixedPayout ? <Spinner width={15} /> : "Set amount →"}
            </Button>
          </form>
        </Form>
      </div>
    </DialogContent>
  );
};
